"use client";

import { ArrowUpRight, Check } from "lucide-react";
import { useLocale, useTranslations } from "next-intl";
import { cn } from "@/lib/utils";
import { courtsMarketingUrl } from "./courts-marketing-url";
import { VenuePlan } from "./venue-plan";

/**
 * The pitch beside the sign-in form: three true benefits, the venue plan, and a link to
 * the full court-booking page on club-landing. Informational only — signing in stays the
 * one primary action on this screen.
 */
export function CourtsPitch({ className }: { className?: string }) {
	const t = useTranslations("pitch");
	const locale = useLocale();
	const benefits = [t("benefitFree"), t("benefitSetup"), t("benefitBooking")];

	return (
		<section
			aria-labelledby="pitch-title"
			className={cn("flex flex-col px-6 py-12 sm:px-10 lg:px-16 lg:pt-6 lg:pb-12", className)}
		>
			<div className="mx-auto flex w-full max-w-156 flex-col gap-8 lg:mx-0 lg:max-w-none lg:flex-1 lg:justify-between">
				<VenuePlan className="w-full lg:h-[min(34dvh,320px)] lg:w-auto lg:max-w-full" />
				<div className="max-w-xl">
					<h2
						className="text-balance font-[650] text-[clamp(2rem,1.4rem+2vw,3.25rem)] leading-[1.04] tracking-[-0.02em]"
						id="pitch-title"
					>
						{t("headline")}
					</h2>
					<ul className="mt-6 grid gap-3 text-base text-muted-foreground">
						{benefits.map((benefit) => (
							<li className="flex items-start gap-3" key={benefit}>
								<Check
									aria-hidden="true"
									className="mt-0.5 size-5 shrink-0 stroke-[2.5] text-verdigris-bright"
								/>
								<span>{benefit}</span>
							</li>
						))}
					</ul>
					<a
						className="mt-5 inline-flex min-h-11 items-center gap-1.5 font-medium text-foreground underline-offset-4 hover:underline"
						href={courtsMarketingUrl(locale)}
					>
						{t("learnMore")}
						<ArrowUpRight aria-hidden="true" className="size-4" />
					</a>
				</div>
			</div>
		</section>
	);
}
